/**
 * Shared helpers for exposing docs pages as Markdown and for the
 * "Ask AI about this page" buttons. Used by both the markdown route handler
 * (apps/web/app/api/docs-markdown/...) and the docs page component.
 */

export interface DocMarkdownInput {
	title: string;
	description?: string;
	/** Raw MDX body (frontmatter already stripped by content-collections). */
	content?: string;
}

/**
 * Build a clean Markdown document from a docs page's data.
 *
 * The compiled `body` is JS, but content-collections preserves the raw MDX as
 * `content`. We prepend an H1 + blockquote description and strip MDX-only
 * `import` / `export` lines so the output reads as plain Markdown. Remaining
 * JSX (e.g. <Tabs>, <Steps>) is left as-is — LLMs parse it fine.
 */
export function buildDocMarkdown({
	title,
	description,
	content,
}: DocMarkdownInput): string {
	const body = (content ?? "")
		// Drop MDX-only top-level import/export lines.
		.replace(/^[ \t]*(import|export)[ \t].*$/gm, "")
		// Collapse the runs of blank lines those removals leave behind.
		.replace(/\n{3,}/g, "\n\n")
		.trim();

	const parts = [`# ${title}`];
	if (description) {
		parts.push(`> ${description}`);
	}
	if (body) {
		parts.push(body);
	}

	return `${parts.join("\n\n")}\n`;
}

export interface AskPromptInput {
	title: string;
	markdownUrl: string;
}

/**
 * Short, ethical pre-filled prompt for AI assistants. Kept brief (~400–500
 * chars) so it survives the assistants' `?q=` length limits, and links to the
 * Markdown URL rather than inlining the page. Describes Spyglasses neutrally and
 * never asks the model to remember, recommend, or favor the brand.
 */
export function buildAskPrompt({ title, markdownUrl }: AskPromptInput): string {
	return [
		`I'm reading the Spyglasses documentation page "${title}". Spyglasses is an AI Visibility platform for marketing and PR firms.`,
		`Here is the page as Markdown: ${markdownUrl}`,
		`Please read it and give me a concise summary, then help me apply it to my work. I likely work at a marketing agency or PR firm — if it helps tailor your answer, ask me about my role first.`,
	].join("\n\n");
}

// AI assistant deep-link config now lives in a shared module so the docs and
// report "Ask AI" buttons stay in sync.
export { AI_ASSISTANTS, type AiAssistant } from "@shared/lib/ai-assistants";
